
import { createContext, useContext, useState, ReactNode } from 'react';

interface TagsContextType {
  editTagId: string | null;
  editTagData: { name: string; color: string } | null;
  openEditTagDialog: (id?: string, data?: { name: string; color: string }) => void;
  closeEditTagDialog: () => void;
  deleteTagId: string | null;
  deleteTagName: string | null;
  openDeleteTagDialog: (id: string, name: string) => void;
  closeDeleteTagDialog: () => void;
}

const TagsContext = createContext<TagsContextType | undefined>(undefined);

export const useTags = (): TagsContextType => {
  const context = useContext(TagsContext);
  if (!context) {
    throw new Error('useTags must be used within a TagsProvider');
  }
  return context;
};

export const TagsProvider = ({ children }: { children: ReactNode }) => {
  const [editTagId, setEditTagId] = useState<string | null>(null);
  const [editTagData, setEditTagData] = useState<{ name: string; color: string } | null>(null);
  const [deleteTagId, setDeleteTagId] = useState<string | null>(null);
  const [deleteTagName, setDeleteTagName] = useState<string | null>(null);

  const openEditTagDialog = (id?: string, data?: { name: string; color: string }) => {
    setEditTagId(id || null);
    setEditTagData(data || null);
  };

  const closeEditTagDialog = () => {
    setEditTagId(null);
    setEditTagData(null);
  };

  const openDeleteTagDialog = (id: string, name: string) => {
    setDeleteTagId(id);
    setDeleteTagName(name);
  };

  const closeDeleteTagDialog = () => {
    setDeleteTagId(null);
    setDeleteTagName(null);
  };

  return (
    <TagsContext.Provider
      value={{
        editTagId,
        editTagData,
        openEditTagDialog,
        closeEditTagDialog,
        deleteTagId,
        deleteTagName,
        openDeleteTagDialog,
        closeDeleteTagDialog,
      }}
    >
      {children}
    </TagsContext.Provider>
  );
};

export default TagsProvider;
